import { format, isValid } from 'date-fns';

import { getTeamLogoUrl } from '@/shared/services/getTeamLogoUrl';

import type { ClipModelFromServer, ClipSeasonFromServer } from '../hooks/useAllClipsQuery';
import { getEventLabel } from './clipEventStyle';

/** Grouping key used when a clip has no event type / no tracked player. */
export const UNGROUPED = 'none';

/** One side of the fixture, for the matchup crest pair on group headers. */
export interface ClipMatchupTeam {
  abbreviation: string;
  logoUrl: string | null;
}

export interface ClipLibraryItem {
  id: string;
  /** Headline shown under the thumbnail. */
  title: string;
  /** "EDM vs FLA · 12/15/25" */
  gameLabel: string;
  /** "P 1 · 12:34 · #97 Connor McDavid C" — empty segments dropped. */
  meta: string;
  /** mm:ss, or null when the clip has no resolved in/out pair. */
  duration: string | null;

  gameKey: string;
  gameTitle: string;
  gameDate: string;
  /** Descending game start, so game groups stay newest-first. */
  gameSortKey: number;

  seasonKey: string;
  seasonLabel: string;

  eventKey: string;
  eventLabel: string;

  playerKey: string;
  playerTitle: string;
  playerSubtitle: string;
  /** Roster portrait for the tracked player; null when there is none. */
  playerHeadshotUrl: string | null;
  // Unjoined parts of playerTitle/playerSubtitle, for rows that lay them out
  // themselves rather than printing one string.
  playerNumber: string;
  playerName: string;
  playerPosition: string;

  /** Jersey + surname for the ISO badge; null for untracked clips. */
  isoLabel: string | null;

  teamAbbreviation: string;
  teamColor: string;
  /** Crest for the card avatar; null falls back to the coloured initials. */
  teamLogoUrl: string | null;

  matchup: { away: ClipMatchupTeam; home: ClipMatchupTeam };

  thumbnailUrl: string | null;
  previewMp4Url: string | null;

  status: string;
}

const FALLBACK_TEAM_COLOR = '#27272E';

const toDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return isValid(parsed) ? parsed : null;
};

const formatDuration = (start?: string | null, end?: string | null): string | null => {
  const from = toDate(start);
  const to = toDate(end);
  if (!from || !to) return null;

  const totalSeconds = Math.max(0, Math.round((to.getTime() - from.getTime()) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/** Primary club colour, shared with the filter pickers so both tint alike. */
export const teamColorOf = (team?: { teamColors?: any } | null): string =>
  team?.teamColors?.[0]?.hex_color || FALLBACK_TEAM_COLOR;

// getTeamLogoUrl yields '' when the team has no logo; normalise to null so the
// card can branch on presence instead of rendering a broken <img>.
const logoUrlOf = (team?: { logoUrl?: string | null } | null): string | null =>
  getTeamLogoUrl(team) || null;

const toMatchupTeam = (
  team?: { abbreviation?: string; logoUrl?: string | null } | null,
): ClipMatchupTeam => ({
  abbreviation: team?.abbreviation || '',
  logoUrl: logoUrlOf(team),
});

/**
 * A league carries one season row per type per year, so 'Regular' is dropped as
 * the unmarked common case while Pre/Post stay qualified.
 */
export const seasonLabelOf = (season?: ClipSeasonFromServer | null): string => {
  const base = season?.name || season?.year || '';
  if (!base) return '';
  return season?.seasonType && season.seasonType !== 'Regular' ? `${base} ${season.seasonType}` : base;
};

export const toClipLibraryItems = (rows: ClipModelFromServer[]): ClipLibraryItem[] =>
  rows.map((row) => {
    const { formatters, game } = row;
    const tracked = formatters?.trackedObject;
    const player = tracked?.type === 'Player' ? tracked : null;

    const gameStart = toDate(game?.startTimestamp);
    const gameDate = gameStart ? format(gameStart, 'MM/dd/yy') : '';
    const gameTitle =
      game?.awayTeam?.abbreviation && game?.homeTeam?.abbreviation
        ? `${game.awayTeam.abbreviation} vs ${game.homeTeam.abbreviation}`
        : 'Unknown game';

    // eventName is the custom event's own description, else the clip group's
    // name — which was seeded from the play-by-play event text at draft time
    // (see resolveEventName). Headlining it means the card names the play, not
    // the clip. The note is the fallback for clips cut outside any event, which
    // have no group to name them; formatters.clipName is skipped because it is
    // only ever `note || 'Clip N'`, both already covered here and staler.
    const title = formatters?.eventName?.trim() || row.note?.trim() || `Clip ${row.id}`;

    const eventKey = formatters?.eventType?.toLowerCase() || UNGROUPED;
    const seasonLabel = seasonLabelOf(game?.season);

    // The tracked player's own team, when we can match it against the fixture —
    // otherwise the home side, which is what the card falls back to showing.
    const trackedTeam =
      player?.playerTeamId && game?.awayTeam?.id === player.playerTeamId
        ? game.awayTeam
        : player?.playerTeamId && game?.homeTeam?.id === player.playerTeamId
          ? game.homeTeam
          : game?.homeTeam;

    const meta = [formatters?.period, formatters?.gameTime, player ? `#${player.playerNumber} ${player.displayName} ${player.playerPosition}` : null]
      .filter(Boolean)
      .join(' · ');

    return {
      id: String(row.id),
      title,
      gameLabel: [gameTitle, gameDate].filter(Boolean).join(' · '),
      meta,
      duration: formatDuration(row.startTimestamp, row.endTimestamp),

      gameKey: String(game?.id ?? game?.uniqueId ?? UNGROUPED),
      gameTitle,
      gameDate,
      gameSortKey: gameStart ? -gameStart.getTime() : 0,

      seasonKey: game?.season?.id ? String(game.season.id) : UNGROUPED,
      seasonLabel: seasonLabel || 'No season',

      eventKey,
      eventLabel: eventKey === UNGROUPED ? 'No event' : getEventLabel(eventKey),

      playerKey: player ? String(player.id) : UNGROUPED,
      playerTitle: player ? `#${player.playerNumber} ${player.displayName}` : 'No tracked player',
      playerSubtitle: player ? [player.playerPosition, trackedTeam?.abbreviation].filter(Boolean).join(' · ') : '',
      playerHeadshotUrl: player ? (row.trackedPlayerHeadshot ?? null) : null,
      playerNumber: player?.playerNumber ?? '',
      playerName: player?.displayName ?? '',
      playerPosition: player?.playerPosition ?? '',

      isoLabel: player ? `#${player.playerNumber} ${player.displayName}` : null,

      teamAbbreviation: trackedTeam?.abbreviation || '',
      teamColor: teamColorOf(trackedTeam),
      teamLogoUrl: logoUrlOf(trackedTeam),

      matchup: { away: toMatchupTeam(game?.awayTeam), home: toMatchupTeam(game?.homeTeam) },

      thumbnailUrl: row.preview?.thumbnailUrl ?? null,
      previewMp4Url: row.preview?.mp4Url ?? null,

      status: row.status,
    };
  });

export default toClipLibraryItems;
